// Storage utilities for robust localStorage handling and desktop compatibility

export interface StorageError {
  type: 'access_denied' | 'quota_exceeded' | 'not_supported' | 'parse_error' | 'unknown';
  message: string;
  originalError?: Error;
}

export const safeGetFromStorage = <T>(key: string, fallback: T): { data: T; error?: StorageError } => {
  try {
    console.log(`📁 Attempting to read from localStorage: ${key}`, {
      platform: window.innerWidth < 768 ? 'mobile' : 'desktop',
      windowWidth: window.innerWidth,
      storageType: typeof localStorage,
      hasPermission: true
    });
    
    // Check if localStorage is available
    if (typeof Storage === 'undefined' || typeof localStorage === 'undefined') {
      console.warn("⚠️ localStorage is not supported");
      return { 
        data: fallback, 
        error: { 
          type: 'not_supported', 
          message: 'localStorage is not supported in this environment' 
        } 
      };
    }
    
    // Test localStorage access specifically
    try {
      localStorage.setItem('test_key', 'test_value');
      localStorage.removeItem('test_key');
      console.log("✅ localStorage access test passed");
    } catch (testError) {
      console.error("❌ localStorage access test failed:", testError);
      return { 
        data: fallback, 
        error: { 
          type: 'access_denied', 
          message: 'localStorage access denied or blocked',
          originalError: testError as Error
        } 
      };
    }
    
    const item = localStorage.getItem(key);
    console.log(`📄 Raw localStorage item for ${key}:`, item ? `found (${item.length} chars)` : 'not found');
    
    if (item === null) {
      console.log(`📭 No data found for key: ${key}, using fallback`);
      return { data: fallback };
    }
    
    const parsed = JSON.parse(item);
    console.log(`✅ Successfully parsed data for ${key}:`, Array.isArray(parsed) ? `Array(${parsed.length})` : typeof parsed);
    return { data: parsed };
    
  } catch (error) {
    console.error(`❌ Error reading from localStorage for key ${key}:`, error);
    
    let storageError: StorageError;
    
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        storageError = { type: 'quota_exceeded', message: 'Storage quota exceeded', originalError: error };
      } else if (error.name === 'SecurityError') {
        storageError = { type: 'access_denied', message: 'Access to localStorage denied', originalError: error };
      } else if (error instanceof SyntaxError) {
        storageError = { type: 'parse_error', message: 'Failed to parse stored data', originalError: error };
      } else {
        storageError = { type: 'unknown', message: error.message, originalError: error };
      }
    } else {
      storageError = { type: 'unknown', message: 'Unknown storage error' };
    }
    
    return { data: fallback, error: storageError };
  }
};

export const safeSetToStorage = (key: string, value: any): { success: boolean; error?: StorageError } => {
  try {
    console.log(`💾 Attempting to save to localStorage: ${key}`);
    
    // Check if localStorage is available
    if (typeof Storage === 'undefined' || typeof localStorage === 'undefined') {
      console.warn("⚠️ localStorage is not supported");
      return { 
        success: false, 
        error: { 
          type: 'not_supported', 
          message: 'localStorage is not supported in this environment' 
        } 
      };
    }
    
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    console.log(`✅ Successfully saved data for ${key}`);
    return { success: true };
    
  } catch (error) {
    console.error(`❌ Error saving to localStorage for key ${key}:`, error);
    
    let storageError: StorageError;
    
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        storageError = { type: 'quota_exceeded', message: 'Storage quota exceeded', originalError: error };
      } else if (error.name === 'SecurityError') {
        storageError = { type: 'access_denied', message: 'Access to localStorage denied', originalError: error };
      } else {
        storageError = { type: 'unknown', message: error.message, originalError: error };
      }
    } else {
      storageError = { type: 'unknown', message: 'Unknown storage error' };
    }
    
    return { success: false, error: storageError };
  }
};

export const getStorageDebugInfo = () => {
  console.log("🔍 Storage Debug Info:");
  console.log("- localStorage supported:", typeof localStorage !== 'undefined');
  console.log("- sessionStorage supported:", typeof sessionStorage !== 'undefined');
  
  if (typeof localStorage !== 'undefined') {
    const keys = Object.keys(localStorage);
    console.log("- localStorage keys:", keys.length);
    console.log("- Auth-related keys:", keys.filter(k => k.includes('supabase') || k.includes('auth') || k.includes('sb-')));
    console.log("- App-related keys:", keys.filter(k => k.includes('createJoy') || k.includes('savedIdeas')));
  }
};